import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Tag,
  List,
  Space,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Upload,
  message,
  Spin,
  Alert,
  Divider,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  UploadOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { OnboardingService, ConnectVerification } from '../services/onboarding';
import { OnboardingForm, FormField } from '../types';

const { Text, Title } = Typography;
const PRIMARY = '#2f6bff';

// 环节状态 -> i18n key
const STEP_STATUS_TAG: Record<string, { color: string; key: string }> = {
  verified: { color: 'success', key: 'stepVerified' },
  approved: { color: 'success', key: 'stepVerified' },
  complete: { color: 'success', key: 'stepVerified' },
  completed: { color: 'success', key: 'stepVerified' },
  pending: { color: 'processing', key: 'stepPending' },
  in_progress: { color: 'processing', key: 'stepInProgress' },
  failed: { color: 'error', key: 'stepFailed' },
  rejected: { color: 'error', key: 'stepFailed' },
};

interface Props {
  // 入驻申请列表中是否存在 stripe_connect 申请；不存在则不请求、不渲染
  hasConnectApplication: boolean;
}

const ConnectVerificationCard: React.FC<Props> = ({ hasConnectApplication }) => {
  const { t } = useTranslation();
  const stepTag = (status: string) =>
    STEP_STATUS_TAG[status]
      ? { color: STEP_STATUS_TAG[status].color, text: t(`connect.${STEP_STATUS_TAG[status].key}`) }
      : { color: 'default', text: status };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ConnectVerification | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [formConfig, setFormConfig] = useState<OnboardingForm | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const fetchVerification = useCallback(async () => {
    if (!hasConnectApplication) return;
    setLoading(true);
    try {
      const result = await OnboardingService.getConnectVerification();
      setData(result);
    } catch (error) {
      // 静默：未配置或暂不可用时不显示卡片
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasConnectApplication]);

  useEffect(() => {
    fetchVerification();
  }, [fetchVerification]);

  // 打开补充资料 Modal，加载表单 schema
  const openSupplementModal = async () => {
    setModalOpen(true);
    setFileList([]);
    form.resetFields();
    setFormLoading(true);
    try {
      const config = await OnboardingService.getOnboardingForm();
      setFormConfig(config);
    } catch (error) {
      message.error(t('connect.loadFormFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  // 根据 schema 字段类型渲染控件
  const renderFieldControl = (field: FormField) => {
    switch (field.type) {
      case 'select':
      case 'radio':
        return (
          <Select placeholder={field.placeholder || t('connect.selectField', { label: field.label })} allowClear>
            {(field.options || []).map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        );
      case 'number':
        return (
          <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} />
        );
      case 'email':
        return <Input type="email" placeholder={field.placeholder} />;
      default:
        return <Input placeholder={field.placeholder} />;
    }
  };

  // 仅渲染可见字段（跳过 hidden）
  const visibleFields = (formConfig?.fields || []).filter(
    (f) => f.type !== 'hidden'
  );

  // 文档上传：使用 multipart，附带 document_type / purpose
  const handleUpload = async (file: File): Promise<boolean> => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'identity_document');
      fd.append('purpose', 'identity_document');
      await OnboardingService.uploadOnboardingDocument(fd);
      message.success(t('connect.uploadSuccess'));
      return true;
    } catch (error) {
      message.error(t('connect.uploadFailed'));
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!data?.application_id) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      // 经我们后端 PUT 提交（Stripe Accounts API，不跳转）
      await OnboardingService.resubmitConnectApplication(data.application_id, values);
      message.success(t('connect.submitSuccess'));
      setModalOpen(false);
      await fetchVerification();
    } catch (error: any) {
      if (error?.errorFields) {
        // 表单校验错误已由 antd 提示
        return;
      }
      message.error(error?.response?.data?.message || t('connect.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 未配置 Connect 申请，或后端返回 configured=false：不显示卡片
  if (!hasConnectApplication) return null;
  if (!loading && (!data || !data.configured)) return null;

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <SafetyCertificateOutlined style={{ color: PRIMARY }} />
          <span>{t('connect.cardTitle')}</span>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={fetchVerification}
        >
          {t('connect.refreshStatus')}
        </Button>
      }
    >
      <Spin spinning={loading}>
        {data?.ready ? (
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
            <Text strong style={{ color: '#52c41a' }}>
              {t('connect.verifiedReady')}
            </Text>
          </Space>
        ) : (
          <>
            {data && data.missing && data.missing.length > 0 && (
              <>
                <Text strong>{t('connect.needMoreInfo')}</Text>
                <List
                  size="small"
                  style={{ marginTop: 12 }}
                  dataSource={data.missing}
                  renderItem={(item) => (
                    <List.Item>
                      <Tag color="warning">{item.label}</Tag>
                    </List.Item>
                  )}
                />
              </>
            )}

            {data && data.steps && data.steps.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Space wrap>
                  {data.steps.map((s) => {
                    const t = stepTag(s.status);
                    return (
                      <Tag key={s.name} color={t.color} title={s.message}>
                        {s.name}：{t.text}
                      </Tag>
                    );
                  })}
                </Space>
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <Button
                type="primary"
                icon={<FormOutlined />}
                onClick={openSupplementModal}
                style={{ background: PRIMARY, borderColor: PRIMARY }}
              >
                {t('connect.supplement')}
              </Button>
              <Text type="secondary" style={{ marginLeft: 12 }}>
                {t('connect.willUpdate')}
              </Text>
            </div>
          </>
        )}
      </Spin>

      <Modal
        title={t('connect.modalTitle')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={t('common.submit')}
        cancelText={t('common.cancel')}
        confirmLoading={submitting}
        okButtonProps={{ style: { background: PRIMARY, borderColor: PRIMARY } }}
        width={640}
        destroyOnClose
      >
        <Spin spinning={formLoading}>
          <Alert
            message={t('connect.modalNote')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form form={form} layout="vertical">
            {visibleFields.map((field) => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={
                  field.required
                    ? [{ required: true, message: t('connect.fillField', { label: field.label }) }]
                    : undefined
                }
              >
                {renderFieldControl(field)}
              </Form.Item>
            ))}
          </Form>

          <Divider />
          <Title level={5}>{t('connect.idUpload')}</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('connect.idUploadDesc')}
          </Text>
          <Upload
            fileList={fileList}
            maxCount={1}
            beforeUpload={async (file) => {
              const ok = await handleUpload(file as File);
              if (ok) {
                setFileList([
                  {
                    uid: String(Date.now()),
                    name: (file as File).name,
                    status: 'done',
                  },
                ]);
              }
              // 阻止 antd 默认上传行为，已由我们手动上传
              return Upload.LIST_IGNORE;
            }}
            onRemove={() => {
              setFileList([]);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              {t('connect.uploadId')}
            </Button>
          </Upload>
        </Spin>
      </Modal>
    </Card>
  );
};

export default ConnectVerificationCard;
